// Site Statistics Web Component

class SiteStats extends HTMLElement {
  constructor() {
    super();
    this.stats = null;
    this.isLoading = true;
    this.currentUser = null;
    this.isLoadingStats = false; // Prevent multiple simultaneous calls
  }

  connectedCallback() {
    this.render();
    this.setupSectionListener();
    this.loadStats();
  }

  disconnectedCallback() {
    // Remove event listener when component is removed
    if (this.handleSectionShown) {
      document.removeEventListener('section-shown', this.handleSectionShown);
    }
  }

  setupSectionListener() {
    // Listen for admin section shown events to load stats when admin section is displayed
    this.handleSectionShown = (event) => {
      if (event.detail.section === 'admin') {
        // Check if we're on the site-stats tab and should load stats
        const siteStatsContent = document.getElementById('site-stats-content');
        const isOnSiteStatsTab = siteStatsContent && siteStatsContent.style.display !== 'none';

        if (isOnSiteStatsTab && this.currentUser && window.API) {
          this.loadStats();
        }
      }
    };
    document.addEventListener('section-shown', this.handleSectionShown);
  }

  isVisible() {
    // Check if this component is currently visible on the page
    // For admin components, check if the admin dashboard is visible
    const adminSection = document.getElementById('admin-section');
    const adminVisible = adminSection && !adminSection.classList.contains('hidden');

    // Also check if the site-stats content div is visible (not hidden by tab switching)
    const siteStatsContent = document.getElementById('site-stats-content');
    const statsContentVisible = siteStatsContent && siteStatsContent.style.display !== 'none';

    return adminVisible && statsContentVisible;
  }

  setCurrentUser(user) {
    this.currentUser = user;

    if (!window.API) {
      // If API is missing, set loading to false and show error state
      this.isLoading = false;
      this.updateContent();
      return;
    }
    // If we're visible and have user data, load stats
    if (this.isVisible() && this.currentUser && window.API) {
      this.loadStats();
    }
  }

  render() {
    this.innerHTML = `
      <div class="site-stats">
        <div class="stats-header">
          <h2>Site Statistics</h2>
          <p>Overview of your Fun Old Times community</p>
        </div>

        <div class="stats-content">
          ${this.renderContent()}
        </div>
      </div>
    `;
  }

  renderContent() {
    if (this.isLoading) {
      return `
        <div class="loading-state">
          <div class="spinner"></div>
          <div>Loading statistics...</div>
        </div>
      `;
    }

    if (!this.stats) {
      return `
        <div class="loading-state">
          <div class="spinner"></div>
          <div>Failed to load statistics</div>
        </div>
      `;
    }

    return `
      <div class="stats-grid">
        <div class="stat-card">
          <div class="stat-icon">👥</div>
          <div class="stat-value">${this.stats.users.total}</div>
          <div class="stat-label">Total Users</div>
          <div class="stat-description">Registered members</div>
        </div>

        <div class="stat-card">
          <div class="stat-icon">👑</div>
          <div class="stat-value">${this.stats.users.admins}</div>
          <div class="stat-label">Administrators</div>
          <div class="stat-description">Admin users</div>
        </div>

        <div class="stat-card">
          <div class="stat-icon">📝</div>
          <div class="stat-value">${this.stats.posts.total}</div>
          <div class="stat-label">Total Posts</div>
          <div class="stat-description">All time posts</div>
        </div>

        <div class="stat-card">
          <div class="stat-icon">🤝</div>
          <div class="stat-value">${this.stats.friendships.total}</div>
          <div class="stat-label">Friendships</div>
          <div class="stat-description">Active connections</div>
        </div>
      </div>

      <div class="recent-activity">
        <h3 class="activity-title">Recent Activity (Last 7 Days)</h3>
        <div class="activity-grid">
          <div class="activity-card">
            <div class="activity-value">${this.stats.users.recent}</div>
            <div class="activity-label">New Users</div>
          </div>
          <div class="activity-card">
            <div class="activity-value">${this.stats.posts.recent}</div>
            <div class="activity-label">New Posts</div>
          </div>
        </div>
      </div>
    `;
  }

  async loadStats() {
    // Prevent multiple simultaneous calls
    if (this.isLoadingStats) {
      return;
    }

    // Only load if user is admin and component is visible
    if (!this.currentUser || this.currentUser.role !== 'admin') {
      return;
    }

    if (!this.isVisible()) {
      return;
    }

    // Check if API is available
    if (!window.API) {
      window.logger.error('API not available');
      return;
    }

    this.isLoadingStats = true;
    this.isLoading = true;
    this.updateContent();

    try {
      this.stats = await window.API.request('/admin/stats');
    } catch (error) {
      window.logger.error('Failed to load site stats:', error);
      this.stats = null;
    } finally {
      this.isLoading = false;
      this.isLoadingStats = false;
      this.updateContent();
    }
  }

  updateContent() {
    const content = this.querySelector('.stats-content');
    if (content) {
      content.innerHTML = this.renderContent();
    }
  }
}

// Register the custom element
customElements.define('site-stats', SiteStats);
